import { userApi, auth } from '@/lib/api';

let deferredInstallPrompt = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferredInstallPrompt = event;
    window.dispatchEvent(new CustomEvent('mb:install-available'));
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('mb:installed'));
  });
}

export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
export const canInstall = () => Boolean(deferredInstallPrompt) && !isStandalone();

export async function installApp() {
  if (!deferredInstallPrompt) return { available: false };
  await deferredInstallPrompt.prompt();
  const choice = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return { available: true, outcome: choice.outcome };
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(char => char.charCodeAt(0)));
}

export function pushCapability() {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  return {
    supported,
    permission: supported ? Notification.permission : 'unsupported',
    iosNeedsInstall: supported && isIos() && !isStandalone()
  };
}

export async function currentPushSubscription() {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function enablePush() {
  const capability = pushCapability();
  if (!capability.supported) throw new Error('Push notifications are unsupported');
  if (capability.iosNeedsInstall) throw new Error('IOS_INSTALL_REQUIRED');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'PERMISSION_DENIED' : 'PERMISSION_DISMISSED');
  const [{ data: config }, registration] = await Promise.all([userApi.get('/push/config'), navigator.serviceWorker.ready]);
  if (!config.enabled || !config.publicKey) throw new Error('PUSH_NOT_CONFIGURED');
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(config.publicKey) });
  await userApi.post('/user/push/subscriptions', { subscription: subscription.toJSON(), deviceName: navigator.userAgentData?.platform || navigator.platform || 'Web browser' });
  return subscription;
}

export async function disablePush() {
  const subscription = await currentPushSubscription();
  if (!subscription) return false;
  await userApi.delete('/user/push/subscriptions', { data: { endpoint: subscription.endpoint } });
  await subscription.unsubscribe();
  return true;
}

export async function syncExistingPushSubscription() {
  if (!auth.getUser() || pushCapability().permission !== 'granted') return null;
  const subscription = await currentPushSubscription();
  if (!subscription) return null;
  await userApi.post('/user/push/subscriptions', { subscription: subscription.toJSON(), deviceName: navigator.userAgentData?.platform || navigator.platform || 'Web browser' });
  return subscription;
}

export async function updateAppBadge(count) {
  const value = Math.max(0, Number(count || 0));
  try {
    if (value === 0 && typeof navigator.clearAppBadge === 'function') await navigator.clearAppBadge();
    else if (value > 0 && typeof navigator.setAppBadge === 'function') await navigator.setAppBadge(value);
    else if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      if (value === 0 && typeof registration.clearAppBadge === 'function') await registration.clearAppBadge();
      else if (value > 0 && typeof registration.setAppBadge === 'function') await registration.setAppBadge(value);
    }
  } catch {}
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') return;
  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' });
      // Long-lived installed windows must check for fixes when reopened. Do not
      // force reload or clear authentication, drafts, IndexedDB or other apps.
      const checkForUpdate = () => { if (document.visibilityState === 'visible') registration.update().catch(() => {}); };
      window.addEventListener('pageshow', checkForUpdate);
      document.addEventListener('visibilitychange', checkForUpdate);
      checkForUpdate();
      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        worker?.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) window.dispatchEvent(new CustomEvent('mb:pwa-updated'));
        });
      });
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data?.type === 'MB_NOTIFICATION_NAVIGATE' && event.data.targetUrl) {
          const target = new URL(event.data.targetUrl, window.location.origin);
          if (target.origin === window.location.origin) window.location.assign(target.href);
        }
        if (event.data?.type === 'MB_PUSH_SUBSCRIPTION_CHANGED') syncExistingPushSubscription().catch(() => {});
        if (event.data?.type === 'MB_NOTIFICATION_RECEIVED') window.dispatchEvent(new CustomEvent('mb:notification-received'));
      });
    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  });
}
